import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check, X } from "lucide-react";
import { Container, Reveal, CTAButton } from "./primitives";

export function StatBand({ label, stats, dark = false }) {
  return (
    <div data-testid="stat-band">
      {label && (
        <p className={`text-sm uppercase tracking-[0.18em] font-semibold mb-8 ${dark ? "ct-text-cyan" : "text-[#0262B4]"}`}>
          {label}
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
        {stats.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="flex flex-col">
              <span className={`font-display font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-none ${dark ? "text-white" : "text-[#1E1E24]"}`}>
                {s.value}
              </span>
              <span className={`mt-3 text-base leading-snug ${dark ? "text-[#EDEFF7]" : "text-[#40424D]"}`}>
                {s.caption}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function BenefitGrid({ items, cols = 3 }) {
  const colClass = cols === 2 ? "md:grid-cols-2" : "md:grid-cols-3";
  return (
    <div className={`grid grid-cols-1 ${colClass} gap-x-10 gap-y-12`} data-testid="benefit-grid">
      {items.map((item, i) => {
        const Icon = (item.icon && Icons[item.icon]) || Icons.Check;
        return (
          <Reveal key={i} delay={i * 0.06}>
            <div className="flex flex-col">
              <div className="w-12 h-12 rounded-2xl ct-grad-navy flex items-center justify-center mb-5 shadow-[0_6px_18px_rgb(2_98_180/0.25)]">
                <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
              </div>
              {item.label && (
                <h3 className="font-display text-xl font-semibold text-[#1E1E24] mb-2">{item.label}</h3>
              )}
              {item.body && <p className="text-lg text-[#40424D] leading-relaxed">{item.body}</p>}
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

export function SegmentCard({ segment, index = 0 }) {
  const Icon = (segment.icon && Icons[segment.icon]) || Icons.ArrowRight;
  return (
    <Reveal delay={index * 0.08}>
      <Link
        to={`/who-we-serve/${segment.slug}`}
        data-testid={`segment-card-${segment.slug}`}
        aria-label={`Explore ${segment.nav} partnership`}
        className="group block h-full bg-white rounded-[24px] p-8 lg:p-9 ct-shadow-card hover:ct-shadow-hover hover:-translate-y-1 transition-all duration-300 ease-out border border-transparent hover:border-[#0262B4]/20"
      >
        <div className="w-12 h-12 rounded-2xl ct-grad-navy flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-105">
          <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
        </div>
        <h3 className="font-display text-2xl font-semibold text-[#1E1E24] mb-3">{segment.nav}</h3>
        <p className="text-base text-[#40424D] leading-relaxed mb-6">{segment.hook}</p>
        <span className="inline-flex items-center gap-2 text-base font-semibold text-[#0262B4]" aria-hidden="true">
          Explore
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </span>
      </Link>
    </Reveal>
  );
}

export function ComparisonBlock({ without, withItems, withTitle = "With Community Tax", withoutTitle = "Without a solution" }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-testid="comparison-block">
      <Reveal>
        <div className="h-full bg-[#EDEFF7] border border-[#BCBFCC] rounded-[24px] p-8 lg:p-10">
          <h3 className="font-display text-xl font-semibold text-[#1E1E24] mb-6">{withoutTitle}</h3>
          <ul className="space-y-4">
            {without.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <X className="w-3.5 h-3.5 text-red-600" strokeWidth={3} />
                </span>
                <span className="text-base text-[#40424D] leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="h-full bg-white border border-[#0262B4]/20 rounded-[24px] p-8 lg:p-10 ct-shadow-hover">
          <h3 className="font-display text-xl font-semibold text-[#1E1E24] mb-6">{withTitle}</h3>
          <ul className="space-y-4">
            {withItems.map((t, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full ct-grad-navy flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </span>
                <span className="text-base text-[#1E1E24] font-medium leading-snug">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}

export function ProcessSteps({ steps }) {
  return (
    <div className="relative" data-testid="process-steps">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
        {steps.map((step, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="relative">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-11 h-11 rounded-full ct-grad-navy text-white flex items-center justify-center font-display font-bold text-lg shrink-0">
                  {i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block flex-1 h-px bg-[#BCBFCC]" />
                )}
              </div>
              <p className="text-sm uppercase tracking-[0.15em] font-semibold text-[#0262B4] mb-2">
                {step.tag}
              </p>
              <h3 className="font-display text-xl font-semibold text-[#1E1E24] mb-3 leading-snug">
                {step.title}
              </h3>
              <p className="text-base text-[#40424D] leading-relaxed mb-4">{step.body}</p>
              <ul className="space-y-2">
                {step.points.map((p, j) => (
                  <li key={j} className="flex items-start gap-2 text-base text-[#40424D]">
                    <Check className="w-4 h-4 text-[#0262B4] mt-1 shrink-0" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function CTABanner({
  headline = "Explore How Tax Debt Representation Fits Into Your Platform",
  body = "Resolve IRS tax debt behind the scenes — keep the relationship, lift conversion, and protect revenue without adding headcount.",
  buttonLabel = "Become a Partner",
}) {
  return (
    <section className="py-6" data-testid="cta-banner">
      <Container>
        <div className="relative overflow-hidden ct-grad-navy rounded-[32px] px-8 py-20 md:py-28 lg:px-20 text-center">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#18D1F8]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#00B4DE]/15 blur-3xl" />
          <div className="relative max-w-3xl mx-auto">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {headline}
            </h2>
            <p className="text-white text-lg leading-relaxed mb-10 opacity-95">{body}</p>
            <CTAButton to="/become-a-partner" variant="light" testId="cta-banner-button">
              {buttonLabel}
            </CTAButton>
          </div>
        </div>
      </Container>
    </section>
  );
}
